/**
 * Live open-positions store — driven by CQG **PositionStatuses** (the broker's AUTHORITATIVE current
 * net positions, decoded in the browser straight off the socket), NOT reconstructed from individual
 * fills.
 *
 * The old fill-reconstruction overlay (optimisticPositionsStore) summed live fills into a net. That
 * created phantom positions: a refresh wiped the in-memory overlay, then a lone SELL (the closing leg)
 * looked like a NEW short → a "double line". The fix, per the user's directive — "Trust what's coming
 * from the CQG websocket; don't trust what's cached in the FE before the refresh" — is to take CQG's
 * own net-position record as the source of truth for WHAT IS OPEN, its qty, and its direction. On a
 * fresh socket CQG re-sends the full position snapshot immediately, so we `reset()` on open and let CQG
 * repopulate it; nothing FE-cached survives the refresh.
 *
 * KEY = `${account} ${symbol}` — the SAME key the BE `/positions` rows are matched on (account NAME =
 * CQG account_id as string). A `setFromPositionStatus(...)` upserts a (signedNet, avgPrice); a signed
 * net of 0 DELETES the key (flat / closed). The BE row then supplies P&L / mark / entry / tick by the
 * same key (see useOpenPositions.mergePositions).
 */

/** One live net position for a (account, symbol): signed qty + qty-weighted average open price. */
export interface LivePosition {
  /** Account NAME = CQG account_id as string (matches OpenPosition.account / the BE row's account). */
  account: string;
  symbol: string;
  /** Signed net qty across CQG's open_positions: + long, − short. Never 0 (a 0 net DELETES the key). */
  signedNet: number;
  /** Qty-weighted average open price across CQG's open positions (price_correct, absolute-qty weighted). */
  avgPrice: number;
}

type Listener = () => void;

const positions = new Map<string, LivePosition>();
const listeners = new Set<Listener>();

// useSyncExternalStore requires getSnapshot to return a referentially-stable value when nothing
// changed (else React loops). We expose an immutable array snapshot, rebuilt only on mutation.
let snapshot: readonly LivePosition[] = [];

function keyOf(account: string, symbol: string): string {
  return `${account} ${symbol}`;
}

function rebuildSnapshot(): void {
  snapshot = Array.from(positions.values());
}

function emit(): void {
  rebuildSnapshot();
  listeners.forEach(l => { try { l(); } catch { /* ignore */ } });
}

export const livePositionsStore = {
  /**
   * Upsert the broker's net position for one (account, symbol). `signedNet` of 0 ⇒ DELETE the key
   * (the position is flat / closed — CQG sent an empty open_positions list or a net-zero one). Returns
   * true when the store actually changed (used by the decoder for logging / re-render gating).
   */
  setFromPositionStatus(account: string, symbol: string, signedNet: number, avgPrice: number): boolean {
    const key = keyOf(account, symbol);
    if (signedNet === 0) {
      if (!positions.has(key)) return false; // already flat → no-op
      positions.delete(key);
      emit();
      return true;
    }
    const prev = positions.get(key);
    if (prev && prev.signedNet === signedNet && prev.avgPrice === avgPrice) return false; // unchanged
    positions.set(key, { account, symbol, signedNet, avgPrice });
    emit();
    return true;
  },

  /** Referentially-stable snapshot of all live positions (for useSyncExternalStore). */
  getSnapshot(): readonly LivePosition[] {
    return snapshot;
  },

  /** Subscribe to changes (useSyncExternalStore-style). Returns an unsubscribe. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },

  /**
   * Wipe the whole store. The relay calls this on a fresh CQG socket (new session): nothing the FE
   * cached before the refresh is trusted — CQG re-sends the full position snapshot immediately, so the
   * store repopulates from the broker's truth. Also used by tests.
   */
  reset(): void {
    positions.clear();
    emit();
  },
};

/** The current live positions (the stable snapshot array). Pure pass-through for tests / consumers. */
export function selectLivePositions(): readonly LivePosition[] {
  return livePositionsStore.getSnapshot();
}
